import { Injectable, inject } from '@angular/core';
import type { ReviewAssetSequenceRenderAsset } from '../../../Review/services/review-image-rendering.models';
import {
  ReviewBrowserRenderProgress,
  ReviewBrowserRenderResult
} from '../../../Review/models/review-browser-render.model';
import { RenderOutputSettings } from '../../../../core/render-runtime';
import { BackgroundRenderClientService } from '../../../../core/render-runtime/worker/background-render-client.service';
import type { RenderJobResult } from '../../../../core/render-runtime/worker/render-job.model';
import { buildAssetSequenceRenderJob } from '../../../Review/services/render-job/assets-render-job.adapter';
import { assetSequenceRenderMaxSeconds } from '../../../../core/constants/runtime.constants';

export interface SmartVideoAssetSequenceRenderOptions {
  title?: string | null;
  description?: string | null;
  assets: ReviewAssetSequenceRenderAsset[];
  aspectRatio?: '9:16' | '16:9';
  fitMode?: 'cover' | 'contain';
  includeSubtitles?: boolean | null;
  outputSettings?: RenderOutputSettings | null;
  skipOptionalAudio?: boolean | null;
  onProgress?: (progress: ReviewBrowserRenderProgress) => void;
  /**
   * Wave 5 (cancel): receives the background-worker cancel function once the render starts, so a
   * caller (the Jivu Command driver) can ABORT the in-flight encode. Optional — the inline page path
   * never sets it, so its behaviour is unchanged.
   */
  onHandle?: (cancel: () => void) => void;
}

@Injectable({ providedIn: 'root' })
export class SmartVideoAssetSequenceRendererService {
  private readonly backgroundRender = inject(BackgroundRenderClientService);

  render(options: SmartVideoAssetSequenceRenderOptions): Promise<ReviewBrowserRenderResult> {
    if (!this.backgroundRender.supported()) {
      throw new Error('This browser cannot render video (WebCodecs/Worker unavailable).');
    }
    return this.renderWithWorker(options);
  }

  /**
   * Background-worker render path. Maps the asset-sequence inputs onto
   * `buildAssetSequenceRenderJob`, drives the background worker, and adapts its
   * `RenderJobResult` back into the EXACT `ReviewBrowserRenderResult` shape the
   * existing upload step consumes. `onProgress` is forwarded unchanged.
   */
  private async renderWithWorker(options: SmartVideoAssetSequenceRenderOptions): Promise<ReviewBrowserRenderResult> {
    const job = buildAssetSequenceRenderJob({
      assets: options.assets,
      aspectRatio: options.aspectRatio ?? '9:16',
      fitMode: options.fitMode ?? 'cover',
      includeSubtitles: options.includeSubtitles === true,
      description: options.description ?? null,
      skipOptionalAudio: options.skipOptionalAudio === true,
      maxSlotSeconds: assetSequenceRenderMaxSeconds(),
      outputSettings: options.outputSettings ?? null
    });

    const totalSeconds = job.timeline.durationSeconds;
    const handle = this.backgroundRender.start(job, p => {
      // Map the worker phases (preparing|rendering|encoding|done) onto the SAME
      // ReviewBrowserRenderProgress the legacy path emits.
      const phase: ReviewBrowserRenderProgress['phase'] =
        p.phase === 'rendering' || p.phase === 'encoding' ? 'rendering' : 'preparing';
      const percent = Math.max(0, Math.min(94, Math.round((p.progress || 0) * 92) + 2));
      const elapsedSeconds = Math.max(0, Math.min(totalSeconds, (p.progress || 0) * totalSeconds));
      options.onProgress?.(
        p.phase === 'rendering'
          ? {
              phase,
              percent,
              message: `Processing ${this.formatSeconds(elapsedSeconds)} of ${this.formatSeconds(totalSeconds)}...`,
              elapsedSeconds,
              totalSeconds
            }
          : { phase, percent, message: p.message || 'Preparing video...' }
      );
    });
    // Hand the worker's cancel up to the caller so a Jivu Command cancel can abort this encode.
    options.onHandle?.(handle.cancel);

    let out: RenderJobResult;
    try {
      out = await handle.result;
    } catch (err) {
      throw err instanceof Error ? err : new Error('Background render failed.');
    }

    const stamp = Date.now();
    const file = new File([out.blob], `assets-library-render-${stamp}.mp4`, { type: out.mimeType || 'video/mp4' });
    const thumbnailFile = out.thumbnails.length
      ? new File([out.thumbnails[0]], `assets-library-render-thumb-${stamp}.jpg`, { type: out.thumbnails[0].type || 'image/jpeg' })
      : null;
    options.onProgress?.({ phase: 'complete', percent: 100, message: 'Video processing complete.', elapsedSeconds: totalSeconds, totalSeconds });

    return {
      file,
      thumbnailFile,
      durationSeconds: out.durationSeconds,
      width: out.width,
      height: out.height,
      mimeType: out.mimeType || 'video/mp4',
      clientRuntime: 'browser'
    };
  }

  private formatSeconds(value: number): string {
    const total = Math.max(0, Math.round(value));
    const minutes = Math.floor(total / 60);
    const seconds = `${total % 60}`.padStart(2, '0');
    return `${minutes}:${seconds}`;
  }
}
